/*
A basic Optional implementation that:
- Intentionally omits a get method
- Doesn't have methods that throw
- Has fake 'pattern matching'
*/

export class Optional<A> {

    private _v: A | null = null

    private static _none = new Optional()

    static from<A>(x: A | null = null): Optional<A> {
        return x == null ? Optional._none as Optional<A> : new Optional<A>(x)
    }

    constructor(x: A | null = null) {
        if (x == null) return Optional._none as Optional<A>
        this._v = x
    }

    isPresent(): boolean {
        return this._v != null
    }

    isEmpty(): boolean {
        return this._v == null
    }

    contains(x: A): boolean {
        return this._v == null ? false : this._v == x
    }

    getOrElse(y: A): A {
        return this._v == null ? y : this._v
    }

    map<B>(f: (x: A) => B): Optional<B> {
        return this._v == null ? this as unknown as Optional<B> : Optional.from<B>(f(this._v))
    }

    flatMap<B>(f: (x: A) => Optional<B>): Optional<B> {
        return this._v == null ? this as unknown as Optional<B> : f(this._v)
    }

    filter(p: (x: A) => boolean): Optional<A> {
        return this._v == null ? Optional._none as Optional<A> : p(this._v) ? this : Optional._none as Optional<A>
    }

    exists(p: (x: A) => boolean): boolean {
        return this._v == null ? false : p(this._v)
    }

    orElse(o: Optional<A>) : Optional<A> {
        return this._v == null ? o : this
    }

    forEach(f: (x: A) => void): void {
        this._v == null || f(this._v)
    }

    match<C, D>({some, none}: {some: (x: A) => C, none: () => D}): C | D {
        return this._v == null ? none() : some(this._v)
    }
}
